import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { InlineInput } from "@air/component-inline-input";
import { TreeItem, TreeItemButton } from "@air/component-tree";
import { Check, Plus } from "@air/next-icons";
import { Button } from "@air/primitive-button";
import { IconButton } from "@air/primitive-icon-button";
import { Tooltip } from "@air/primitive-tooltip";
import constant from "lodash/constant";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { useBoardPermissions } from "~/hooks/useBoardPermissions";
import { useFetchObjectsPermissions } from "~/hooks/useFetchObjectsPermissions";
import { useCreateNewBoard } from "~/swr-hooks/boards/useCreateNewBoard";
import { useSubBoardsList } from "~/swr-hooks/boards/useSubBoardsList";
import { canCreateBoard } from "~/utils/permissions/boardPermissions";
const SubBoardLabel = "Create New Sub-Board";
export const BoardSearchListItem = /*#__PURE__*/ memo((param)=>{
    let { board, onBoardSelectChange, isBoardSelected, initialBoardId, shouldShowSubBoards = constant(false), getIsBoardDisabled } = param;
    var _board_library;
    const [showSubBoards, setShowSubBoards] = useState(null);
    const [isCreatingSubBoard, setIsCreatingSubBoard] = useState(false);
    const containerRef = useRef(null);
    const isSelected = !!(isBoardSelected === null || isBoardSelected === void 0 ? void 0 : isBoardSelected(board));
    const isDisabled = getIsBoardDisabled === null || getIsBoardDisabled === void 0 ? void 0 : getIsBoardDisabled(board);
    const { boardPermissions } = useBoardPermissions({
        boardId: board.id
    });
    const { data: subBoards, isInitialLoading: isLoadingSubBoards, hasMore, loadNextPage } = useSubBoardsList(board.id, Boolean(showSubBoards));
    useFetchObjectsPermissions({
        objects: {
            boardIds: subBoards.map((board)=>board.id)
        }
    });
    const { createNewBoard } = useCreateNewBoard();
    const createBoard = useCallback((boardName)=>{
        var _board_library;
        return createNewBoard({
            board: {
                title: boardName,
                parentId: board.id,
                libraryId: (_board_library = board.library) === null || _board_library === void 0 ? void 0 : _board_library.id
            },
            trackLocation: "board-select-modal",
            shouldFollow: true
        });
    }, [
        createNewBoard,
        board.id,
        (_board_library = board.library) === null || _board_library === void 0 ? void 0 : _board_library.id
    ]);
    const handleSubBoardCreation = useCallback((boardName)=>{
        setIsCreatingSubBoard(false);
        if (boardName) {
            createBoard(boardName);
        }
    }, [
        createBoard
    ]);
    const draftNewSubBoard = useCallback(()=>{
        setIsCreatingSubBoard(true);
        setShowSubBoards(true);
    }, []);
    useEffect(()=>{
        const shouldScrollIntoView = initialBoardId === board.id;
        if (isSelected && shouldScrollIntoView && containerRef.current) {
            containerRef.current.scrollIntoView();
        }
    }, [
        isSelected,
        initialBoardId,
        board.id
    ]);
    useEffect(()=>{
        const userHasSetShowSubBoardsIntentionally = showSubBoards !== null;
        if (shouldShowSubBoards(board.id) && !userHasSetShowSubBoardsIntentionally) {
            setShowSubBoards(true);
        }
    }, [
        board.id,
        shouldShowSubBoards,
        showSubBoards
    ]);
    const treeElement = /*#__PURE__*/ _jsx(TreeItem, {
        "data-title": board.title,
        onOpenChange: (isOpen)=>{
            setShowSubBoards(isOpen);
            if (isOpen) onBoardSelectChange({
                board,
                selected: false
            });
        },
        className: isSelected ? "bg-indigo-1" : "",
        suffix: isSelected ? /*#__PURE__*/ _jsx(Check, {
            className: "flex size-4 shrink-0 items-center text-blue-11"
        }) : canCreateBoard(boardPermissions) ? /*#__PURE__*/ _jsx("div", {
            className: "hidden group-focus-within/treeItem:flex group-hover/treeItem:flex group-active/treeItem:flex",
            children: /*#__PURE__*/ _jsx(Tooltip, {
                label: SubBoardLabel,
                side: "right",
                children: /*#__PURE__*/ _jsx(IconButton, {
                    "data-testid": "BOARD_SEARCH_NEW_SUB_BOARD_PLUS_BUTTON",
                    icon: Plus,
                    size: "small",
                    appearance: "ghost",
                    color: "grey",
                    onClick: draftNewSubBoard,
                    label: SubBoardLabel
                })
            })
        }) : null,
        children: /*#__PURE__*/ _jsx(TreeItemButton, {
            className: !!isDisabled ? "opacity-60" : "",
            onClick: ()=>onBoardSelectChange({
                    board,
                    selected: !isSelected
                }),
            disabled: !!isDisabled,
            children: board.title
        })
    });
    return /*#__PURE__*/ _jsxs("div", {
        ref: containerRef,
        "data-testid": "BOARD_SEARCH_TREE_ITEM",
        "data-title": board.title,
        children: [
            !!isDisabled ? /*#__PURE__*/ _jsx(Tooltip, {
                label: isDisabled.message,
                side: "left",
                sideOffset: 0,
                children: /*#__PURE__*/ _jsx("div", {
                    children: treeElement
                })
            }) : treeElement,
            !isCreatingSubBoard && showSubBoards && !subBoards.length && !isLoadingSubBoards && /*#__PURE__*/ _jsx(Button, {
                onClick: draftNewSubBoard,
                className: "ml-4 px-4",
                size: "small",
                appearance: "ghost",
                color: "grey",
                children: SubBoardLabel
            }),
            (showSubBoards || isCreatingSubBoard) && /*#__PURE__*/ _jsxs("div", {
                className: "pl-4",
                children: [
                    isCreatingSubBoard && /*#__PURE__*/ _jsx(InlineInput, {
                        rows: 1,
                        allowNewLines: false,
                        label: "Board name",
                        autoFocus: true,
                        className: "my-1 ml-6",
                        onSubmit: (value)=>{
                            handleSubBoardCreation(value);
                        },
                        "data-testid": "BOARD_SEARCH_NEW_SUB_BOARD_EDITABLE_TEXT"
                    }),
                    subBoards.map((s)=>{
                        return /*#__PURE__*/ _jsx("div", {
                            children: /*#__PURE__*/ _jsx(BoardSearchListItem, {
                                shouldShowSubBoards: shouldShowSubBoards,
                                initialBoardId: initialBoardId,
                                isBoardSelected: isBoardSelected,
                                onBoardSelectChange: onBoardSelectChange,
                                board: s,
                                getIsBoardDisabled: getIsBoardDisabled
                            })
                        }, s.id);
                    }),
                    !isLoadingSubBoards && hasMore && /*#__PURE__*/ _jsx(Button, {
                        onClick: ()=>loadNextPage(),
                        className: "ml-4 px-4",
                        size: "small",
                        appearance: "ghost",
                        color: "grey",
                        children: "Show more"
                    })
                ]
            })
        ]
    });
});
BoardSearchListItem.displayName = "BoardSearchListItem";
